/**
 * Upload Settings Hook
 *
 * React Query hook for fetching and updating upload settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/api.client';
import type { ApiResponse } from '@/types/api.types';

export interface UploadSettings {
  max_file_size_mb: number;
  max_voice_message_size_mb: number;
  max_files_per_upload: number;
  allowed_file_types: string[];
  enable_file_compression: boolean;
}

export type SettingValue = string | number | boolean | string[];

export interface SystemSetting {
  value: SettingValue;
  type: string;
  description: string;
  category: string;
}

export interface AllSettings {
  [key: string]: SystemSetting;
}

/**
 * Fetch upload settings (public endpoint, no auth required)
 */
export const useUploadSettings = () => {
  return useQuery<UploadSettings>({
    queryKey: ['uploadSettings'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<{ settings: UploadSettings }>>(
        '/settings/upload'
      );
      return response.data.data!.settings;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetch all system settings (requires auth, admins see all, users see public only)
 */
export const useSystemSettings = (category?: string) => {
  return useQuery<AllSettings>({
    queryKey: ['systemSettings', category],
    queryFn: async () => {
      const params = category ? `?category=${category}` : '';
      const response = await apiClient.get<ApiResponse<{ settings: AllSettings }>>(
        `/settings${params}`
      );
      return response.data.data!.settings;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update a single setting (admin only)
 */
export const useUpdateSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: SettingValue }) => {
      const response = await apiClient.put(`/settings/${key}`, { value });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all settings queries
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
      queryClient.invalidateQueries({ queryKey: ['uploadSettings'] });
    },
  });
};

/**
 * Update multiple settings (admin only)
 */
export const useUpdateSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Record<string, SettingValue>) => {
      const response = await apiClient.patch('/settings', { settings });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all settings queries
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
      queryClient.invalidateQueries({ queryKey: ['uploadSettings'] });
    },
  });
};
